import angular from 'angular';
import {sprintf} from '@/angular/libs/sprintf';

/**
 * Turns a floating-point number of hours to the format {HH}h{MM}m. The hour and minutes are left out when zero.
 * If the raw value equals zero then '0m' is returned.
 * @param {number} hoursFloat
 * @returns {string}
 */
function printHumanHours(hoursFloat) {
    if (hoursFloat === 0) return '0m';
    if (hoursFloat < 0) return '-' + printHumanHours(-hoursFloat);
    const hours = Math.floor(hoursFloat);
    const minutes = Math.floor( hoursFloat * 60) % 60;
    return (hours ? `${hours}h ` : '') + (minutes ? `${minutes}m` : '');
}

/**
 * Takes a number of seconds and returns the format {HH}h{MM}m.
 * @param {number} seconds
 * @returns {string}
 */
 function printHumanSeconds(seconds) {
    return printHumanHours(seconds / 3600);
}

angular.module('vrm.components')
    .directive('vrmSummaryWidget', vrmSummaryWidget)
    .service('AdvancedSummaryWidgetConfigs', /* @ngInject */  (UserService) => ({
        "Status": {
            extended: {
                "advanced.ac_input": [
                    ['IV1', 'II1', 'IP1'],
                    ['IV2', 'II2', 'IP2'],
                    ['IV3', 'II3', 'IP3']
                ],
                "advanced.ac_output": [
                    ['OV1', 'OI1', 'OP1'],
                    ['OV2', 'OI2', 'OP2'],
                    ['OV3', 'OI3', 'OP3']
                ],
                "advanced.dc": [
                    ['CV', 'CI']
                ],
                "advanced.battery_temperature": function($scope) {
                    if (!$scope.getField('CT')) return [];
                    return [$scope.getField('CT').valueFormattedWithUnit];
                },
                "advanced.hours_of_ac": function($scope) {
                    if ($scope.extra.hours) {
                        const rawHours = $scope.extra.hours.records.data;
                        const timeLabel = printHumanHours(rawHours);
                        return [timeLabel];
                    }
                    return ['-'];
                }
            },
            urls: {
                "hours": "widgets/HoursOfAC"
            },
            status: "S",
            hide: [
                "CT", // Already used in dynamic field
            ]
        },
        "PVInverterStatus": {
            extended: {
                "advanced.ac_input": [
                    ['pV1', 'pI1', 'pP1'],
                    ['pV2', 'pI2', 'pP2'],
                    ['pV3', 'pI3', 'pP3']
                ],
                "advanced.ac_total": function($scope) {
                    //Parse float field if exists
                    function parseField(fieldName) {
                        var field = $scope.getField(fieldName);
                        if (field) {
                            return parseFloat(field.valueFloat);
                        } else {
                            return 0;
                        }
                    }

                    var totalI = _.sum(_.map(['pI1', 'pI2', 'pI3'], parseField));
                    var totalP = _.sum(_.map(['pP1', 'pP2', 'pP3'], parseField));

                    if (totalI === parseField('pI1') || totalI === 0)
                    {
                        return [];
                    }
                    return [null, totalI.toFixed(2) + " A", Math.floor(totalP) + " W"];
                },
                "advanced.energy": [
                    ['pE1'], ['pE2'], ['pE3']
                ]
            },
            status: "pS"
        },
        "BatterySummary": {
            extended: {
                "advanced.voltage_and_current": [
                    ['V', 'I']
                ],
                "advanced.power": function($scope) {
                    var value = null;
                    if ($scope.getField("V") && $scope.getField("I")) {
                        value = (parseFloat($scope.getField("V").valueFloat) * parseFloat($scope.getField("I").valueFloat)).toFixed(2) + " W";
                    }

                    return [value];
                },
                "advanced.mid_voltage": function($scope) {
                    var value = null;
                    if ($scope.getField("VM") && $scope.getField("VMD")) {
                        var top = sprintf($scope.getField("V").formatWithUnit.replace('F', 'f'), ($scope.getField("V").valueFloat - $scope.getField("VM").valueFloat));
                        var bot =  $scope.getField("VM").valueFormattedWithUnit;
                        var dev = $scope.getField("VMD").valueFormattedWithUnit;
                        value = "Top: " + top  +"; Bot: " + bot + "; Dev: " + dev;
                    }
                    return value ? value.split(';') : [];
                },
                "advanced.voltage_min_max": function ($scope) {
                    const fields = ["mcV", "McV"];
                    const result = [];
                    for (const attr of fields) {
                        const fieldData = $scope.getField(attr);
                        if (!fieldData) return [];
                        result.push(fieldData.valueFormattedWithUnit);
                    }
                    return result;
                },
            },
            subdata: {
                "SOC": function($scope) {
                    const ttgAttribute = $scope.getField("TTG");
                    const ttg = parseFloat(ttgAttribute.valueFloat);
                    if (ttg > 0) {
                        return {
                            dataAttributeName: ttgAttribute.dataAttributeName,
                            valueFormattedWithUnit: printHumanHours(ttg),
                        };
                    } else {
                        return null;
                    }
                }
            },
            hide: [
                "VM", "VMD", "TTG", // Already used in dynamic field
                "mcV", "McV", // Used in advanced.voltage_min_max
                "AL", "AH", "ALS", "AHS", "ASoc", "ALT", "AHT", "AM"
            ]
        },
        "TankSummary": {
            // Show tank capacity in M3 if more than 1000 L and user doesn't use gallons
            extended: {
                "advanced.tank_capacity": function($scope) {
                    var value = null, unit = null;
                    if ($scope.getField("tc")) {
                        value = $scope.getField("tc").valueFormattedValueOnly;
                        unit = $scope.getField("tc").valueFormattedWithUnit.split(' ')[1];
                        if (!UserService.user.useGallons && parseFloat(value) >= 1000) {
                            value = Math.round(parseFloat(value) / 1000) + ' M3';
                        } else if (parseFloat(value) > 1000) {
                            value = Math.round(parseFloat(value)) + ' ' + unit;
                        } else {
                            value = $scope.getField("tc").formattedValue
                        }
                    }
                    return value ? [value] : [];
                },
                "advanced.tank_remaining_fluid": function($scope) {
                    var value = null, unit = null;
                    if ($scope.getField("tr")) {
                        value = $scope.getField("tr").valueFormattedValueOnly;
                        unit = $scope.getField("tr").valueFormattedWithUnit.split(' ')[1];
                        if (!UserService.user.useGallons && parseFloat(value) >= 1000) {
                            value = Math.round(parseFloat(value) / 1000) + ' M3';
                        } else if (parseFloat(value) > 1000) {
                            value = Math.round(parseFloat(value)) + ' ' + unit;
                        } else {
                            value = $scope.getField("tr").formattedValue;
                        }
                    }
                    return value ? [value] : [];
                }
            },
            hide: [
                "tr","tc"
            ]
        },
        "EvChargerSummary": {
            extended: {
                "advanced.ev_charger_charged_energy": function($scope) {
                    if (!$scope.extra.delta || !$scope.extra.delta.records.evE) return ['-'];
                    const rawDeltas = Object.values($scope.extra.delta.records.evE.data).filter(delta => delta !== null);
                    if (rawDeltas.length === 0) return ['-'];
                    const totalDelta = rawDeltas.reduce((acc, delta) => acc + delta, 0);
                    const formattedDelta = sprintf($scope.extra.delta.records.evE.formatWithUnit.replace('F', 'f'), totalDelta);
                    return [formattedDelta];
                },
            },
            urls: {
                "delta": "widgets/EvChargerSummary?onlyDeltaAttributes=1"
            },
        },
        "GridMeterSummary": {
            extended: {
                "advanced.ac_phase_l1": [
                    ['g1v', 'g1c', 'g1p']
                ],
                "advanced.ac_phase_l2": [
                    ['g2v', 'g2c', 'g2p']
                ],
                "advanced.ac_phase_l3": [
                    ['g3v', 'g3c', 'g3p']
                ],
                "advanced.frequency": [
                    ['g1F']
                ],
            },
        },
        "GridMeterEnergySummary": {
            extended: {
                "advanced.energy_forward": function($scope) {
                    if (!$scope.extra.delta || !$scope.extra.delta.records.dgb) return ['-'];
                    const rawDeltas = Object.values($scope.extra.delta.records.dgb.data).filter(delta => delta !== null);
                    if (rawDeltas.length === 0) return ['-'];
                    const totalDelta = rawDeltas.reduce((acc, delta) => acc + delta, 0);
                    const formattedDelta = sprintf($scope.extra.delta.records.dgb.formatWithUnit.replace('F', 'f'), totalDelta);
                    return [formattedDelta];
                },
                "advanced.energy_reverse": function($scope) {
                    if (!$scope.extra.delta || !$scope.extra.delta.records.dgs) return ['-'];
                    const rawDeltas = Object.values($scope.extra.delta.records.dgs.data).filter(delta => delta !== null);
                    if (rawDeltas.length === 0) return ['-'];
                    const totalDelta = rawDeltas.reduce((acc, delta) => acc - delta, 0);
                    const formattedDelta = sprintf($scope.extra.delta.records.dgs.formatWithUnit.replace('F', 'f'), totalDelta);
                    return [formattedDelta];
                },
                "advanced.total_energy_forward": [
                    ['gf']
                ],
                "advanced.energy_forward_l1": [
                    ['g1f'],
                ],
                "advanced.energy_forward_l2": [
                    ['g2f'],
                ],
                "advanced.energy_forward_l3": [
                    ['g3f'],
                ],
                "advanced.total_energy_reverse": [
                    ['gr']
                ],
                "advanced.energy_reverse_l1": [
                    ['g1r'],
                ],
                "advanced.energy_reverse_l2": [
                    ['g2r'],
                ],
                "advanced.energy_reverse_l3": [
                    ['g3r'],
                ],
            },
            urls: {
                "delta": "widgets/GridMeterEnergySummary?onlyDeltaAttributes=1"
            },
        },
        "PvInverterSummary": {
            extended: {
                "advanced.ac_phase_l1": [
                    ['pV1', 'pI1', 'pP1']
                ],
                "advanced.ac_phase_l2": [
                    ['pV2', 'pI2', 'pP2']
                ],
                "advanced.ac_phase_l3": [
                    ['pV3', 'pI3', 'pP3']
                ],
            },
        },
        "PvInverterEnergySummary": {
            extended: {
                "advanced.energy_forward": function($scope) {
                    if (!$scope.extra.delta || !$scope.extra.delta.records.dpE) return ['-'];
                    const rawDeltas = Object.values($scope.extra.delta.records.dpE.data).filter(delta => delta !== null);
                    if (rawDeltas.length === 0) return ['-'];
                    const totalDelta = rawDeltas.reduce((acc, delta) => acc + delta, 0);
                    const formattedDelta = sprintf($scope.extra.delta.records.dpE.formatWithUnit.replace('F', 'f'), totalDelta);
                    return [formattedDelta];
                },
                "advanced.total_energy_forward": [
                    ['pf']
                ],
                "advanced.energy_forward_l1": [
                    ['pE1'],
                ],
                "advanced.energy_forward_l2": [
                    ['pE2'],
                ],
                "advanced.energy_forward_l3": [
                    ['pE3'],
                ],
            },
            urls: {
                "delta": "widgets/PvInverterEnergySummary?onlyDeltaAttributes=1"
            },
        },
        "AcMeterSummary": {
            extended: {
                "advanced.ac_phase_l1": [
                    ['a1v', 'a1c', 'a1p']
                ],
                "advanced.ac_phase_l2": [
                    ['a2v', 'a2c', 'a2p']
                ],
                "advanced.ac_phase_l3": [
                    ['a3v', 'a3c', 'a3p']
                ],
                "advanced.frequency": [
                    ['aF']
                ],
            },
        },
        "AcMeterEnergySummary": {
            extended: {
                "advanced.energy_forward": function($scope) {
                    if (!$scope.extra.delta || !$scope.extra.delta.records.daE) return ['-'];
                    const rawDeltas = Object.values($scope.extra.delta.records.daE.data).filter(delta => delta !== null);
                    if (rawDeltas.length === 0) return ['-'];
                    const totalDelta = rawDeltas.reduce((acc, delta) => acc + delta, 0);
                    const formattedDelta = sprintf($scope.extra.delta.records.daE.formatWithUnit.replace('F', 'f'), totalDelta);
                    return [formattedDelta];
                },
                "advanced.total_energy_forward": [
                    ['af']
                ],
                "advanced.energy_forward_l1": [
                    ['a1f'],
                ],
                "advanced.energy_forward_l2": [
                    ['a2f'],
                ],
                "advanced.energy_forward_l3": [
                    ['a3f'],
                ],
            },
            urls: {
                "delta": "widgets/AcMeterEnergySummary?onlyDeltaAttributes=1"
            },
        },
    }));

const M3_TO = {
    LITRE: 1000,
    GALLON: 264.172052
};

/* @ngInject */
function vrmSummaryWidget() {

        return {
            restrict: 'E',
            template: require('@/angular/modules/vrm-components/summary-widget/summary-widget.template.html'),
            controller: controller,
            scope: {
                iconClass: '=',
                customName: '=',
                installation: '=',
                type: '=',
                instance: '=',
                title: '=',
                subTitle: '=',
                tooltip: '=',
                isMobile: '=',
                hasTouchEnabled: '=',
                size: '=',
                range: '='
            }
        };

    /* @ngInject */
    function controller(MQTTRealtimeService, UserService, $scope, ApiService, AdvancedSummaryWidgetConfigs, vrmConfig, WhitelabelService, $stateParams) {
        //Create list of used fields for extended
        $scope.extended = [];
        if (AdvancedSummaryWidgetConfigs[$scope.type]) {
            $scope.extended.push(AdvancedSummaryWidgetConfigs[$scope.type].status);
            var i;
            for (i in AdvancedSummaryWidgetConfigs[$scope.type].extended) {
                if (typeof AdvancedSummaryWidgetConfigs[$scope.type].extended[i] !== "function") {
                    for (var j in AdvancedSummaryWidgetConfigs[$scope.type].extended[i]) {
                        for (var n in AdvancedSummaryWidgetConfigs[$scope.type].extended[i][j]) {
                            $scope.extended.push(AdvancedSummaryWidgetConfigs[$scope.type].extended[i][j][n]);
                        }
                    }
                }
            }

            for (i in AdvancedSummaryWidgetConfigs[$scope.type].subdata) {
                if (typeof AdvancedSummaryWidgetConfigs[$scope.type].subdata[i] !== "function") {
                    $scope.extended.push(AdvancedSummaryWidgetConfigs[$scope.type].subdata[i]);
                }
            }

            for (i in AdvancedSummaryWidgetConfigs[$scope.type].hide) {
                $scope.extended.push(AdvancedSummaryWidgetConfigs[$scope.type].hide[i]);
            }
        }

        $scope.widgetConfig = AdvancedSummaryWidgetConfigs[$scope.type];
        $scope.extra = {}; //Extra data from other API's

        $scope.attributeOrder = [];

        $scope.sortAttributes = function(record) {
            // Sort by the current attribute order (retrieved from API)
            return $scope.attributeOrder.indexOf(parseInt(record.idDataAttribute));
        };

        var convertValues = function(attributeRecord)
        {
            // If code is undefined, skip the attribute
            if (typeof attributeRecord.code === "undefined"){
                return attributeRecord;
            }
            // H9 is in days, but should be shown as hh:mm if last charge
            // is less than 1 day which can not be done using sprintf
            if (attributeRecord.code === "H9") {
                if(attributeRecord.value < 1) { //Last charge was less than one day!
                    var valueInSeconds = attributeRecord.value * 86400;
                    var hours = (valueInSeconds / 3600);
                    var rhours = Math.floor(hours);
                    var minutes = (hours - rhours) * 3600 / 60;
                    var rminutes = Math.round(minutes);
                    attributeRecord.valueFormattedWithUnit = rhours.toString().padStart(2, "0") +":"+rminutes.toString().padStart(2, "0");
                }
                else { //Displaying it as hours and days if H9 (number of days) is greater than 1
                    const days = Math.floor(attributeRecord.value); // Get the whole days
                    const hours = Math.round((attributeRecord.value - days) * 24); // Convert the remaining fraction to hours
                    attributeRecord.valueFormattedWithUnit = `${days} day(s) and ${hours} hour(s)`;
                 }
            } else if (attributeRecord.code === "evt") {
                attributeRecord.valueFormattedWithUnit = printHumanSeconds(attributeRecord.value);
            } else if (
                (attributeRecord.code === "tc" || attributeRecord.code === "tr")
                && attributeRecord.alwaysM3) {
                if (UserService.user.useGallons) {
                    attributeRecord.valueFormattedValueOnly = +attributeRecord.valueFormattedValueOnly * M3_TO.GALLON;
                } else {
                    attributeRecord.valueFormattedValueOnly = +attributeRecord.valueFormattedValueOnly * M3_TO.LITRE;
                }
            }
            return attributeRecord;
        };

        $scope.$watch('size', () => {
            if (!$scope.chart || !$scope.chart.getHighcharts) return;
            $scope.chart.getHighcharts().reflow();
        });

        $scope.$watch('installation', function(installation) {
            if (installation != null) {
                var url = 'widgets/' + $scope.type;
                if ($scope.instance) {
                    url += '?instance=' + $scope.instance;
                }
                ApiService.installations(installation.id, url).then(function(response) {
                    $scope.data = _.clone(response.data);
                    $scope.attributeOrder = response.data.records.attributeOrder;
                    $scope.noDataFound = _.find(
                        $scope.data.records.data,
                        item => item.code !== undefined
                    ) === undefined;
                    var data = {};
                    // Set up MQTT for realtime data
                    _.each(response.data.records.data, function(record, key) {
                        record = convertValues(record);
                        if (!record.code)
                        {
                            data[key] = record;
                            return;
                        }
                        if (!record.dataType || !record.dbusPath) {
                            data[record.code] = record;
                            return;
                        }
                        record.mqttFetcher = _.bind(function(message) {
                            // Skip formatting if payload value is null
                            if (message.payload.value === null) return;

                            var value = MQTTRealtimeService.convertValue(this.code, message.payload.value);
                            var isEnum = false;
                            if (typeof this.dataAttributeEnumValues !== "undefined")
                            {
                                _.each(this.dataAttributeEnumValues, function(enumVal) {
                                    if(enumVal.valueEnum == value)
                                    {
                                        value = enumVal.nameEnum;
                                        isEnum = true;
                                    }
                                });
                            }
                            // Calculate value
                            if (record.code === "tc" || record.code === "tr") {
                                // indicate that data attribute will always come in m3 unit format
                                this.alwaysM3 = true;
                            }
                            this.valueFormattedValueOnly = isEnum ? value : sprintf(this.formatValueOnly.replace('F', 'f'), value);
                            this.valueFormattedWithUnit = isEnum ? value : sprintf(this.formatWithUnit.replace('F', 'f'), value);
                            convertValues(this);
                        }, record);

                        if (vrmConfig.enableRealtime)
                        {
                            MQTTRealtimeService.subscribeToPath({
                                identifier: $scope.installation.identifier,
                                dbusServiceType: record.dbusServiceType,
                                dbusPath: record.dbusPath,
                                instance: record.instance,
                                callback: record.mqttFetcher,
                                callbackIdentifier: "summarywidget_"+$scope.type
                            });
                        }
                        data[record.code] = record;
                    });

                    $scope.data.records.data = data;
                });
            }
        });

        $scope.$watchGroup(['range.startDate', 'range.endDate'], function () {
            if ($scope.widgetConfig && $scope.range.startDate && $scope.range.endDate) {
                for (const index in $scope.widgetConfig.urls) {
                    ApiService.installations(
                        $stateParams.id,
                        $scope.widgetConfig.urls[index],
                        {instance: $scope.instance, start: $scope.range.startDate, end: $scope.range.endDate}
                    ).then(function (response) {
                        $scope.extra[index] = response.data;
                    });
                }
            }
        });

        /**
         * Get type of item
         * @param item
         * @returns {string}
         */
        $scope.typeOf = function(item) {
            return typeof item;
        };

        /**
         * Get a field from the retrieved data
         * @param fieldName
         * @returns {*}
         */
        $scope.getField = function(fieldName) {
            if ($scope.data && $scope.data.records) {
                return _.find($scope.data.records.data, {code: fieldName});
            } else {
                return null;
            }
        };

        /**
         * Return if field is a current value
         * @param format
         */
        $scope.isCurrent = function (format) {
            //Check on A in format unit and exclude VA
            const colors = WhitelabelService.getGraphColors();
            if (
                format &&
                format.indexOf("A") >= 0 &&
                format.indexOf("V") === -1
            ) {
                return { color: colors["orange"] };
            } else {
                return { color: colors["blue"] };
            }
        };

        $scope.hasData = function (item) {
            const itemType = typeof item;
            if (itemType === 'object') {
                return item.some(row => row.some(field => $scope.data !== undefined && $scope.data.records.data[field]));
            } else if (itemType === 'function') {
                return true;
            }
        };
    }
}
